import { useRef, useEffect, useState, useCallback } from 'react';
import { Video, VideoOff, User } from 'lucide-react';

/**
 * WebcamPanel — live mirrored camera feed with on/off toggle.
 * Falls back to avatar placeholder if permission denied.
 */
export default function WebcamPanel({ studentName, cameraOn, onToggleCamera, onPermissionChange }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [hasPermission, setHasPermission] = useState(null); // null=pending, true/false

  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      setHasPermission(true);
      onPermissionChange?.(true);
    } catch {
      setHasPermission(false);
      onPermissionChange?.(false);
    }
  }, [onPermissionChange]);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  useEffect(() => {
    if (cameraOn) {
      startCamera();
    } else {
      stopCamera();
    }
    return stopCamera;
  }, [cameraOn, startCamera, stopCamera]);

  const initials = studentName
    ? studentName.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2)
    : '??';

  return (
    <div className="relative w-full h-full rounded-2xl overflow-hidden bg-[#14161B]" data-testid="webcam-panel">
      {/* Video */}
      {cameraOn && hasPermission !== false && (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="absolute inset-0 w-full h-full object-cover"
          style={{ transform: 'scaleX(-1)' }}
        />
      )}

      {/* Fallback placeholder */}
      {(!cameraOn || hasPermission === false) && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
          <div className="w-16 h-16 rounded-full bg-[#3D1F6E]/30 flex items-center justify-center">
            <User className="w-8 h-8 text-[#A78BFA]" />
          </div>
          <span className="text-xs text-[#9BA1B0]">
            {hasPermission === false ? 'Camera access needed' : 'Camera off'}
          </span>
        </div>
      )}

      {/* Name label */}
      <div className="absolute bottom-3 left-3 px-2.5 py-1 rounded-lg bg-black/50 backdrop-blur-sm text-xs text-white font-medium">
        {studentName || 'You'}
      </div>

      {/* Camera toggle */}
      <button
        onClick={onToggleCamera}
        data-testid="camera-toggle-btn"
        className={`absolute top-3 right-3 w-8 h-8 rounded-lg flex items-center justify-center
          transition-colors ${cameraOn ? 'bg-white/10 hover:bg-white/20' : 'bg-red-500/80 hover:bg-red-500'}`}
      >
        {cameraOn ? <Video className="w-4 h-4 text-white" /> : <VideoOff className="w-4 h-4 text-white" />}
      </button>
    </div>
  );
}
